import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshStandardNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Pulsing neon emission stripes on `MeshStandardNodeMaterial`. */
@EngineClass('NeonPulseNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Neon Pulse',
  nodeMaterialGroup: 'TSL Node FX',
})
export class NeonPulseNodeMaterialAsset extends NodeMaterialAsset(MeshStandardNodeMaterial) {

  @property({ type: 'color', description: 'Base material color' })
  override color = new THREE.Color(0.02, 0.02, 0.02);

  @property({ type: 'color', description: 'Neon emission color' })
    neonColor = new THREE.Color(0.0, 1.0, 0.5);

  @property({ type: 'number', min: 0.1, max: 10.0, step: 0.1, description: 'Pulse frequency (Hz)' })
    pulseSpeed = 2.0;

  @property({ type: 'number', min: 0.1, max: 20.0, step: 0.1, description: 'Stripe frequency along Y' })
    stripeFrequency = 10.0;

  @property({ type: 'number', min: 0.1, max: 10.0, step: 0.1, description: 'Stripe scroll speed' })
    stripeSpeed = 3.0;

  private readonly _pulseSpeedUniform = TSL.uniform(this.pulseSpeed);
  private readonly _stripeFrequencyUniform = TSL.uniform(this.stripeFrequency);
  private readonly _stripeSpeedUniform = TSL.uniform(this.stripeSpeed);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.pulseSpeed = this.clampFiniteNumber(this.pulseSpeed, 0.1, 10.0, 2.0);
    this.stripeFrequency = this.clampFiniteNumber(this.stripeFrequency, 0.1, 20.0, 10.0);
    this.stripeSpeed = this.clampFiniteNumber(this.stripeSpeed, 0.1, 10.0, 3.0);
    this._pulseSpeedUniform.value = this.pulseSpeed;
    this._stripeFrequencyUniform.value = this.stripeFrequency;
    this._stripeSpeedUniform.value = this.stripeSpeed;
    this.roughnessNode = TSL.float(1.0);
    this.metalnessNode = TSL.float(0.0);
    this.colorNode = null;

    const pulse = TSL.oscSine(TSL.time.mul(this._pulseSpeedUniform));
    const stripes = TSL.sin(TSL.positionLocal.y.mul(this._stripeFrequencyUniform).add(TSL.time.mul(this._stripeSpeedUniform))).mul(TSL.float(0.5)).add(TSL.float(0.5));
    const neonColorNode = TSL.color(this.neonColor);
    this.emissiveNode = neonColorNode.mul(stripes).mul(pulse.mul(TSL.float(1.5)).add(TSL.float(0.3)));
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): NeonPulseNodeMaterialAsset {
    const instance = new NeonPulseNodeMaterialAsset();
    NeonPulseNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
